import { Fragment, useMemo } from "react";
import { useRecoilValue } from "recoil";
import { Ac2AccessGroupModel } from "src/api/access/ac-group-types";
import { BaseButton, BaseTooltip } from "src/components";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import { globalPartnerState } from "src/recoil/partners/atom";
import { PaPartner } from "src/types/partner";
import { visitorSettings } from "../../const";
type Props = {
  accessGroup: Ac2AccessGroupModel;
  onChangeEdit: (type: "basicInfo" | "setting") => void;
  isAuthority: boolean;
};

const AcGroupSetting = ({ accessGroup, onChangeEdit, isAuthority }: Props) => {
  const partner = useRecoilValue(globalPartnerState);

  // 파트너 feature > CTRLROOM_ACCESS_GROUP_ACCESS_FLOORS 여부 판단 (엘리베이터 층수 제어 노출 여부)
  const isFloorAccessFeatureActive = useMemo(() => {
    return partner?.features?.some((feature: PaPartner) => feature.name === "CTRLROOM_ACCESS_GROUP_ACCESS_FLOORS" && feature.active) || false;
  }, [partner]);

  const {
    memberAccessYn, // 이용자 출입
    visitorAccessYn, // 방문자 출입
    visitorAccessGroupSelectableYn, // Taap 노출 여부
    visitorInvitationApprovalYn, // 방문자 초대 관리자 승인 필요
    visitorAccessType, // QR 발급 타입
    floorAccessYn, // 엘리베이터 연동
    accessibleFloors, // 이용 가능 층
  } = accessGroup;

  return (
    <article className="contents-container__wrap-article">
      <div className="flex-center-between">
        <BaseSectionTitle title="설정" />
        {isAuthority && <BaseButton title="수정" className="size-small" onClick={() => onChangeEdit("setting")} />}
      </div>
      <section className="contents-container__grid">
        <div className="contents-container__grid-index">
          <div className="flex-center">
            <p>이용자 출입 가능 여부</p>
            <BaseTooltip className="ml2" contents="비활성화 하는 경우, 해당 출입그룹 - 출입장치에 이용자가 출입할 수 없습니다." touchIcon="QUESTION" />
          </div>
        </div>
        <div className="contents-container__grid-contents">
          <div className="minmax400">
            <span>{memberAccessYn ? "ON" : "OFF"}</span>
          </div>
        </div>
      </section>
      <section className="contents-container__grid">
        <div className="contents-container__grid-index">
          <div className="flex-center">
            <p>방문자 초대 가능 여부</p>
            <BaseTooltip className="ml2" contents="비활성화 하는 경우, 해당 출입그룹에 방문자를  초대할 수 없습니다." touchIcon="QUESTION" />
          </div>
        </div>
        <div className="contents-container__grid-contents">
          <div>
            <span>{visitorAccessYn ? "ON" : "OFF"}</span>
          </div>
        </div>
      </section>

      <section className="contents-container__grid">
        <div className="contents-container__grid-index"></div>
        <div className="contents-container__grid-contents">
          <section className="contents-container__grid gray-box mt10 mb30" style={{ height: 180 }}>
            {visitorSettings.map((group) => (
              <Fragment key={group.name}>
                <div className="contents-container__grid-index">
                  <div className="flex-center">
                    <p>{group.title}</p>
                    <BaseTooltip className="ml2" contents={group.tooltip} touchIcon="QUESTION" />
                  </div>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="flex-center-start flex-row">
                    {group.options.map((option) => (
                      <span key={option.label}>
                        {(group.name === "visitorAccessGroupSelectableYn" && option.value === visitorAccessGroupSelectableYn && option.label) ||
                          (group.name === "visitorInvitationApprovalYn" && option.value === visitorInvitationApprovalYn && option.label) ||
                          (group.name === "visitorAccessType" && option.value === visitorAccessType && option.label)}
                      </span>
                    ))}
                  </div>
                </div>
              </Fragment>
            ))}
          </section>
        </div>
      </section>

      {isFloorAccessFeatureActive && (
        <>
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <div className="flex-center">
                <p>엘리베이터 연동</p>
                <BaseTooltip className="ml2" touchIcon="QUESTION">
                  <ul className="base-list">
                    <li>이 출입그룹으로 누를 수 있는 엘리베이터 층 버튼을 입력하세요.</li>
                    <li>설치 시 등록된 층 이름과 정확히 일치해야 합니다. (예: B1,1,2,3)</li>
                  </ul>
                </BaseTooltip>
              </div>
            </div>
            <div className="contents-container__grid-contents">
              <div>
                <span>{floorAccessYn ? "ON" : "OFF"}</span>
              </div>
            </div>
          </section>
          <section className="contents-container__grid">
            <div className="contents-container__grid-index"></div>
            <div className="contents-container__grid-contents">
              <section className="contents-container__grid gray-box">
                <div className="contents-container__grid-index pt0">
                  <p>이용 가능 층</p>
                </div>
                <div className="contents-container__grid-contents">
                  <span className="break-all">{accessibleFloors && accessibleFloors.length > 0 ? accessibleFloors.join(",") : "-"}</span>
                </div>
              </section>
            </div>
          </section>
        </>
      )}
    </article>
  );
};

export default AcGroupSetting;
